import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { IncidentType, LocalizedString } from '@/types';

import IncidentIcon from '@/assets/icons/incident.svg?react';
import IncidentTiming from '@/components/Incidents/incident-details/incident-timing';
import { SEARCH_PARAM_INCIDENT_ID } from '@/utils/constants/queries';

interface IncidentSummaryProps {
  incident: IncidentType;
}

const IncidentSummary: FC<IncidentSummaryProps> = ({ incident }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();

  const currentLanguage = i18n.resolvedLanguage as keyof LocalizedString;
  const { gtfsAlertId, activePeriod, headerText, descriptionText } = incident;

  const onHandleClick = useCallback(() => {
    searchParams.set(SEARCH_PARAM_INCIDENT_ID, String(gtfsAlertId));
    setSearchParams(searchParams);
  }, [searchParams, gtfsAlertId]);

  return (
    <div
      onKeyDown={(event) => event.stopPropagation()}
      role="button"
      className="flex items-start justify-between gap-2.5 pb-3 border-b border-surface-border-default w-full max-w-full cursor-pointer pt-3"
      tabIndex={0}
      onClick={onHandleClick}
      aria-label={headerText[currentLanguage]}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 shrink-0 bg-secondary shadow-none flex items-center justify-center rounded-md">
          <IncidentIcon className="size-5" />
        </div>
        <div className="flex flex-col justify-start gap-1.25 min-w-0 w-full">
          <h3 className="font-semibold text-base text-foreground break-word line-clamp-1">
            {headerText[currentLanguage]}
          </h3>
          <p className="font-normal text-sm text-foreground line-clamp-2 wrap-anywhere">
            {descriptionText[currentLanguage]}
          </p>
          <IncidentTiming periods={activePeriod} isListItem />
        </div>
      </div>
    </div>
  );
};

export default IncidentSummary;
